import { toast } from "sonner";
import { Save, Plus, Trash2, ChevronUp, ChevronDown, Eye } from "lucide-react";
import { mergeSaveCmsField } from "@/lib/api";
import { useCms } from "@/lib/cms";
import { Header } from "./Schools";
import { useEditable, Field, Input, TextArea, ImageUpload } from "./Content";
import { Footer } from "@/components/Brand";

export default function FooterCms() {
  const { reload } = useCms();
  const [d, setD, baselineRef] = useEditable("/cms/site");
  if (!d) return <div className="text-gray-500">Loading…</div>;

  const set = (k, v) => setD({ ...d, [k]: v });
  const setSocial = (k, v) => setD({ ...d, social: { ...(d.social || {}), [k]: v } });
  // Merge only the fields this page actually changed onto the latest live
  // document, instead of overwriting the whole record with this session's
  // possibly-stale copy — prevents an unrelated Footer save from resetting
  // header/nav settings edited elsewhere.
  const save = async () => {
    const merged = await mergeSaveCmsField("/cms/site", d, baselineRef.current);
    setD(merged);
    baselineRef.current = merged;
    await reload();
    toast.success("Footer saved");
  };

  const columns = d.footer_columns || [];
  const updateColumn = (i, patch) => setD({ ...d, footer_columns: columns.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const moveColumn = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= columns.length) return;
    const next = [...columns];
    [next[i], next[j]] = [next[j], next[i]];
    setD({ ...d, footer_columns: next });
  };
  const addColumn = () => setD({ ...d, footer_columns: [...columns, { title: "New column", visible: true, links: [] }] });
  const removeColumn = (i) => setD({ ...d, footer_columns: columns.filter((_, idx) => idx !== i) });

  const updateLink = (ci, li, patch) => {
    const links = columns[ci].links.map((l, idx) => (idx === li ? { ...l, ...patch } : l));
    updateColumn(ci, { links });
  };
  const moveLink = (ci, li, dir) => {
    const links = [...columns[ci].links];
    const j = li + dir;
    if (j < 0 || j >= links.length) return;
    [links[li], links[j]] = [links[j], links[li]];
    updateColumn(ci, { links });
  };
  const addLink = (ci) => updateColumn(ci, { links: [...(columns[ci].links || []), { label: "New link", url: "/", visible: true }] });
  const removeLink = (ci, li) => updateColumn(ci, { links: columns[ci].links.filter((_, idx) => idx !== li) });

  return (
    <div className="fade-up">
      <Header title="Footer Management" subtitle="Control every part of the site footer without touching code." actions={
        <button onClick={save} data-testid="footer-save" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2.5 rounded-lg">
          <Save className="w-4 h-4" /> Save changes
        </button>
      } />

      <div className="mt-6 grid gap-6">
        {/* Branding */}
        <Section title="Branding">
          <div className="rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-700">
            <strong>Footer Company Name</strong> is shown only in the footer. Leave blank to automatically use the
            Organisation Name from <a href="/admin/website-identity" className="underline">Website Identity</a>.
            It does NOT affect SEO, emails or certificates.
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Footer Company Name (optional)">
              <Input value={d.footer_company_name || ""} placeholder={d.company_name || "Leave blank to use Organisation Name"}
                onChange={(e) => set("footer_company_name", e.target.value)} />
            </Field>
            <Field label="Footer description"><TextArea value={d.footer_description || ""} onChange={(e) => set("footer_description", e.target.value)} /></Field>
          </div>
          <div className="rounded-xl bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
            <strong>Footer Logo</strong> is independent from the header logo. Leave blank to reuse the same logo as the header.
          </div>
          <Field label="Footer logo (optional — leave blank to reuse header logo)">
            <ImageUpload value={d.footer_logo_url || ""} onChange={(v) => set("footer_logo_url", v)} testid="footer-logo-upload" />
          </Field>

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Width (px)">
              <Input type="number" value={d.footer_logo_width ?? d.footer_logo_size ?? 24}
                onChange={(e) => set("footer_logo_width", Number(e.target.value))} />
            </Field>
            <Field label="Height (px)">
              <Input type="number" value={d.footer_logo_height ?? d.footer_logo_size ?? 24}
                onChange={(e) => set("footer_logo_height", Number(e.target.value))} />
            </Field>
            <Field label="Max width (px, optional)">
              <Input type="number" value={d.footer_logo_max_width ?? ""} placeholder="none"
                onChange={(e) => set("footer_logo_max_width", e.target.value ? Number(e.target.value) : null)} />
            </Field>
            <Field label="Scale (e.g. 1 = 100%)">
              <Input type="number" step="0.1" value={d.footer_logo_scale ?? 1}
                onChange={(e) => set("footer_logo_scale", Number(e.target.value) || 1)} />
            </Field>
            <Field label="Alignment">
              <select value={d.footer_logo_align || "left"} onChange={(e) => set("footer_logo_align", e.target.value)}
                className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full bg-white">
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </Field>
            <Field label="Footer company name size (px)">
              <Input type="number" value={d.footer_company_name_size ?? 18} onChange={(e) => set("footer_company_name_size", Number(e.target.value))} />
            </Field>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Margin (px)</div>
            <div className="grid grid-cols-4 gap-3">
              {[["Top", "footer_logo_margin_top"], ["Right", "footer_logo_margin_right"], ["Bottom", "footer_logo_margin_bottom"], ["Left", "footer_logo_margin_left"]].map(([lbl, key]) => (
                <Field key={key} label={lbl}>
                  <Input type="number" value={d[key] ?? 0} onChange={(e) => set(key, Number(e.target.value))} />
                </Field>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Padding (px)</div>
            <div className="grid grid-cols-4 gap-3">
              {[["Top", "footer_logo_padding_top"], ["Right", "footer_logo_padding_right"], ["Bottom", "footer_logo_padding_bottom"], ["Left", "footer_logo_padding_left"]].map(([lbl, key]) => (
                <Field key={key} label={lbl}>
                  <Input type="number" value={d[key] ?? 0} onChange={(e) => set(key, Number(e.target.value))} />
                </Field>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-500">
            Tablet and mobile widths below override the desktop width above only for their own breakpoint — leave blank to inherit desktop.
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Tablet width override (px, optional)">
              <Input type="number" value={d.footer_logo_tablet_width ?? ""} placeholder="inherit desktop"
                onChange={(e) => set("footer_logo_tablet_width", e.target.value ? Number(e.target.value) : null)} />
            </Field>
            <Field label="Mobile width override (px, optional)">
              <Input type="number" value={d.footer_logo_mobile_width ?? ""} placeholder="inherit desktop"
                onChange={(e) => set("footer_logo_mobile_width", e.target.value ? Number(e.target.value) : null)} />
            </Field>
          </div>

          <label className="block">
            <div className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={d.footer_logo_mobile !== false}
                onChange={(e) => set("footer_logo_mobile", e.target.checked)} />
              Show logo on mobile
            </div>
            <div className="text-xs text-gray-400 mt-0.5 ml-5">Uncheck to hide the logo below 640px (independent from the width override above)</div>
          </label>
        </Section>

        {/* Contact */}
        <Section title="Contact">
          <div className="grid sm:grid-cols-4 gap-4">
            <Field label="Address"><Input value={d.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
            <Field label="Phone"><Input value={d.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
            <Field label="WhatsApp number"><Input value={d.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+91 98765 43210" /></Field>
            <Field label="Email"><Input value={d.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} /></Field>
          </div>
          <p className="text-xs text-gray-400 mt-1">WhatsApp number appears in the site footer, a floating chat button on every page, and on the Contact and Booking pages. Leave blank to hide.</p>
        </Section>

        {/* Social */}
        <Section title="Social links">
          <div className="grid sm:grid-cols-5 gap-2">
            {["linkedin", "twitter", "instagram", "youtube", "facebook"].map((k) => (
              <Input key={k} placeholder={k} value={(d.social || {})[k] || ""} onChange={(e) => setSocial(k, e.target.value)} />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">Leave blank to hide an icon.</p>
        </Section>

        {/* Navigation columns */}
        <Section title="Navigation columns">
          <div className="grid gap-4">
            {columns.map((col, ci) => (
              <div key={ci} className="rounded-xl border border-gray-200 p-4">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Input value={col.title} onChange={(e) => updateColumn(ci, { title: e.target.value })} className="flex-1 min-w-[160px]" />
                  <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                    <input type="checkbox" checked={col.visible !== false} onChange={(e) => updateColumn(ci, { visible: e.target.checked })} /> Show
                  </label>
                  <button onClick={() => moveColumn(ci, -1)} disabled={ci === 0} className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => moveColumn(ci, 1)} disabled={ci === columns.length - 1} className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                  <button onClick={() => removeColumn(ci)} className="w-8 h-8 grid place-items-center rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="grid gap-2">
                  {(col.links || []).map((l, li) => (
                    <div key={li} className="flex flex-wrap items-center gap-2">
                      <Input placeholder="Label" value={l.label} onChange={(e) => updateLink(ci, li, { label: e.target.value })} className="flex-1 min-w-[120px]" />
                      <Input placeholder="/url" value={l.url} onChange={(e) => updateLink(ci, li, { url: e.target.value })} className="flex-1 min-w-[120px]" />
                      <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                        <input type="checkbox" checked={l.visible !== false} onChange={(e) => updateLink(ci, li, { visible: e.target.checked })} /> Show
                      </label>
                      <button onClick={() => moveLink(ci, li, -1)} disabled={li === 0} className="w-7 h-7 grid place-items-center rounded-lg border border-gray-200 disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
                      <button onClick={() => moveLink(ci, li, 1)} disabled={li === col.links.length - 1} className="w-7 h-7 grid place-items-center rounded-lg border border-gray-200 disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
                      <button onClick={() => removeLink(ci, li)} className="w-7 h-7 grid place-items-center rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <button onClick={() => addLink(ci)} className="inline-flex items-center gap-1 text-xs font-medium text-[#3B68E7] w-fit"><Plus className="w-3.5 h-3.5" /> Add link</button>
                </div>
              </div>
            ))}
            <button onClick={addColumn} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3B68E7] w-fit"><Plus className="w-4 h-4" /> Add column</button>
          </div>
        </Section>

        {/* CTA */}
        <Section title="Footer CTA">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Heading"><Input value={d.footer_cta_heading || ""} onChange={(e) => set("footer_cta_heading", e.target.value)} /></Field>
            <Field label="Description"><Input value={d.footer_cta_description || ""} onChange={(e) => set("footer_cta_description", e.target.value)} /></Field>
            <Field label="Button text"><Input value={d.footer_cta_button_text || ""} onChange={(e) => set("footer_cta_button_text", e.target.value)} /></Field>
            <Field label="Button URL"><Input value={d.footer_cta_button_url || ""} onChange={(e) => set("footer_cta_button_url", e.target.value)} /></Field>
          </div>
          <p className="text-xs text-gray-400 mt-1">Leave heading and description blank to hide the CTA bar.</p>
        </Section>

        {/* Newsletter */}
        <Section title="Newsletter">
          <label className="inline-flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" checked={!!d.footer_newsletter_show} onChange={(e) => set("footer_newsletter_show", e.target.checked)} />
            Show newsletter block
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Heading"><Input value={d.footer_newsletter_heading || ""} onChange={(e) => set("footer_newsletter_heading", e.target.value)} /></Field>
            <Field label="Description"><Input value={d.footer_newsletter_description || ""} onChange={(e) => set("footer_newsletter_description", e.target.value)} /></Field>
          </div>
        </Section>

        {/* Copyright */}
        <Section title="Copyright">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Copyright text"><Input placeholder={`© ${new Date().getFullYear()} ${d.company_name || "Your Company"}. All Rights Reserved.`} value={d.footer_copyright_text || ""} onChange={(e) => set("footer_copyright_text", e.target.value)} /></Field>
            <Field label="Privacy policy link"><Input value={d.footer_privacy_url || ""} onChange={(e) => set("footer_privacy_url", e.target.value)} /></Field>
            <Field label="Terms link"><Input value={d.footer_terms_url || ""} onChange={(e) => set("footer_terms_url", e.target.value)} /></Field>
          </div>
        </Section>

        {/* Layout */}
        <Section title="Layout">
          <div className="grid sm:grid-cols-3 gap-4">
            <ColorField label="Background color" value={d.footer_bg_color || "#F8FAFC"} onChange={(v) => set("footer_bg_color", v)} />
            <ColorField label="Text color" value={d.footer_text_color || "#64748B"} onChange={(v) => set("footer_text_color", v)} />
            <ColorField label="Link color" value={d.footer_link_color || "#64748B"} onChange={(v) => set("footer_link_color", v)} />
            <ColorField label="Link hover color" value={d.footer_link_hover_color || "#3B68E7"} onChange={(v) => set("footer_link_hover_color", v)} />
            <ColorField label="Divider color" value={d.footer_divider_color || "#E2E8F0"} onChange={(v) => set("footer_divider_color", v)} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <Field label="Section spacing (px)"><Input type="number" value={d.footer_section_spacing ?? 80} onChange={(e) => set("footer_section_spacing", Number(e.target.value))} /></Field>
            <Field label="Column gap (px)"><Input type="number" value={d.footer_column_gap ?? 40} onChange={(e) => set("footer_column_gap", Number(e.target.value))} /></Field>
          </div>
        </Section>

        {/* Live preview — the real Footer component, fed the unsaved draft */}
        <Section title="Live preview" icon={<Eye className="w-4 h-4" />}>
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <Footer previewSite={d} />
          </div>
        </Section>

        <button onClick={save} data-testid="footer-save-bottom" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2.5 rounded-lg w-fit">
          <Save className="w-4 h-4" /> Save changes
        </button>
      </div>
    </div>
  );
}

const Section = ({ title, icon, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 grid gap-4 max-w-4xl">
    <div className="flex items-center gap-2 text-sm font-semibold text-[#1A202C]">{icon}{title}</div>
    {children}
  </div>
);

const ColorField = ({ label, value, onChange }) => (
  <Field label={label}>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-[42px] rounded-lg border border-gray-200 cursor-pointer shrink-0" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  </Field>
);
